import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import {
  AlertTriangle,
  CalendarDays,
  Clock3,
  FileText,
  Search,
  User,
  Wallet,
  Wrench,
} from "lucide-react";

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function formatMoney(amount, currency) {
  return formatCurrency(amount, currency, "-");
}

function startOfToday() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

function daysFromToday(dateString) {
  if (!dateString) return null;

  const target = new Date(`${dateString}T00:00:00`);
  const diff = target.getTime() - startOfToday().getTime();
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

function dateLabel(dateString) {
  const days = daysFromToday(dateString);
  if (days === null) return "No date";
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`;
  return `In ${days} day${days === 1 ? "" : "s"}`;
}

function eventTone(type, days) {
  if (days !== null && days < 0) return "bg-rose-100 text-rose-700";
  if (type === "Maintenance") return "bg-orange-100 text-orange-700";
  if (type === "Lease") return "bg-sky-100 text-sky-700";
  if (type === "Payment") return "bg-emerald-100 text-emerald-700";
  return "bg-amber-100 text-amber-800";
}

function eventIcon(type) {
  if (type === "Invoice") return FileText;
  if (type === "Payment") return Wallet;
  if (type === "Lease") return User;
  if (type === "Maintenance") return Wrench;
  return CalendarDays;
}

export default function CalendarPage({ isDark, setIsDark, currency, setCurrency }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  const fetchCalendarEvents = async () => {
    setLoading(true);

    const [
      { data: invoiceData, error: invoiceError },
      { data: paymentData, error: paymentError },
      { data: tenantData, error: tenantError },
      { data: ticketData, error: ticketError },
    ] = await Promise.all([
      supabase
        .from("invoices")
        .select("id, tenant, property, unit, amount, due_date, status")
        .order("due_date", { ascending: true }),
      supabase
        .from("payments")
        .select("id, tenant_name, property_name, unit, amount, due_date, status")
        .order("due_date", { ascending: true }),
      supabase
        .from("tenants")
        .select("id, full_name, property_name, unit, lease_end, status")
        .not("lease_end", "is", null)
        .order("lease_end", { ascending: true }),
      supabase
        .from("maintenance_tickets")
        .select("id, issue, property, unit, reported_on, priority, status")
        .order("reported_on", { ascending: false }),
    ]);

    if (invoiceError) console.error("Error fetching invoice calendar events:", invoiceError);
    if (paymentError) console.error("Error fetching payment calendar events:", paymentError);
    if (tenantError) console.error("Error fetching lease calendar events:", tenantError);
    if (ticketError) console.error("Error fetching maintenance calendar events:", ticketError);

    const nextEvents = [
      ...(invoiceData || []).map((invoice) => ({
        id: `invoice-${invoice.id}`,
        type: "Invoice",
        title: `${invoice.status} invoice ${invoice.id}`,
        detail: `${invoice.tenant} - ${invoice.property} - Unit ${invoice.unit}`,
        amount: Number(invoice.amount || 0),
        date: invoice.due_date,
        path: "/invoices",
      })),
      ...(paymentData || []).map((payment) => ({
        id: `payment-${payment.id}`,
        type: "Payment",
        title: `${payment.status} payment`,
        detail: `${payment.tenant_name} - ${payment.property_name} - Unit ${payment.unit}`,
        amount: Number(payment.amount || 0),
        date: payment.due_date,
        path: "/payments",
      })),
      ...(tenantData || []).map((tenant) => ({
        id: `lease-${tenant.id}`,
        type: "Lease",
        title: "Lease expiry",
        detail: `${tenant.full_name} - ${tenant.property_name} - Unit ${tenant.unit}`,
        date: tenant.lease_end,
        path: "/tenants",
      })),
      ...(ticketData || []).map((ticket) => ({
        id: `ticket-${ticket.id}`,
        type: "Maintenance",
        title: `${ticket.priority} maintenance`,
        detail: `${ticket.issue} - ${ticket.property} - Unit ${ticket.unit}`,
        date: ticket.reported_on,
        path: "/maintenance",
      })),
    ];

    setEvents(
      nextEvents.sort((a, b) => {
        const aDays = daysFromToday(a.date);
        const bDays = daysFromToday(b.date);
        return (aDays ?? 9999) - (bDays ?? 9999);
      })
    );
    setLoading(false);
  };

  useEffect(() => {
    fetchCalendarEvents();
  }, []);

  const summary = useMemo(() => {
    const overdue = events.filter((event) => {
      const days = daysFromToday(event.date);
      return days !== null && days < 0;
    }).length;
    const nextSevenDays = events.filter((event) => {
      const days = daysFromToday(event.date);
      return days !== null && days >= 0 && days <= 7;
    }).length;

    return {
      total: events.length,
      overdue,
      nextSevenDays,
      leases: events.filter((event) => event.type === "Lease").length,
    };
  }, [events]);

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      const matchesQuery = `${event.title} ${event.detail} ${event.type}`
        .toLowerCase()
        .includes(query.toLowerCase());
      const matchesType = typeFilter === "All" || event.type === typeFilter;
      return matchesQuery && matchesType;
    });
  }, [events, query, typeFilter]);

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Schedule"
      title="Calendar"
      description="Track rent due dates, payment follow-ups, lease expiries, and maintenance events from one operational calendar."
      actions={
        <button
          type="button"
          onClick={fetchCalendarEvents}
          className="inline-flex h-10 items-center justify-center rounded-xl bg-orange-500 px-3 text-sm font-medium text-white transition hover:bg-orange-600"
        >
          Refresh
        </button>
      }
    >
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {[
          { label: "Scheduled Items", value: summary.total, icon: CalendarDays, tone: "bg-sky-100 text-sky-700" },
          { label: "Overdue", value: summary.overdue, icon: AlertTriangle, tone: "bg-rose-100 text-rose-700" },
          { label: "Next 7 Days", value: summary.nextSevenDays, icon: Clock3, tone: "bg-amber-100 text-amber-800" },
          { label: "Lease Dates", value: summary.leases, icon: User, tone: "bg-emerald-100 text-emerald-700" },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item.label}</p>
                  <h3 className={`mt-3 text-3xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{item.value}</h3>
                </div>
                <div className={`rounded-2xl p-3 ${item.tone}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className={`rounded-[2rem] border p-4 shadow-sm md:p-5 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search calendar..."
              className={`h-12 pl-11 pr-4 ${isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-800"}`}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {["All", "Invoice", "Payment", "Lease", "Maintenance"].map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setTypeFilter(type)}
                className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                  typeFilter === type
                    ? "bg-sky-100 text-sky-700"
                    : isDark
                    ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className={`rounded-[2rem] border p-5 shadow-sm ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        {loading ? (
          <div className={isDark ? "text-slate-300" : "text-slate-600"}>Loading calendar...</div>
        ) : filteredEvents.length === 0 ? (
          <div className={isDark ? "text-slate-300" : "text-slate-600"}>No calendar items found.</div>
        ) : (
          <div className="space-y-3">
            {filteredEvents.map((event) => {
              const Icon = eventIcon(event.type);
              const days = daysFromToday(event.date);

              return (
                <a
                  key={event.id}
                  href={event.path}
                  className={`grid gap-4 rounded-3xl border p-4 text-sm transition md:grid-cols-[auto_1fr_auto] md:items-center ${
                    isDark
                      ? "border-slate-800 bg-slate-950 hover:bg-slate-800"
                      : "border-slate-200 bg-slate-50 hover:bg-white"
                  }`}
                >
                  <div className={`rounded-2xl p-3 ${eventTone(event.type, days)}`}>
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{event.title}</p>
                      <Badge className={eventTone(event.type, days)}>{event.type}</Badge>
                    </div>
                    <p className={`mt-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>{event.detail}</p>
                  </div>

                  <div className="text-left md:text-right">
                    <p className={`font-semibold ${days !== null && days < 0 ? "text-rose-600" : isDark ? "text-white" : "text-slate-900"}`}>
                      {dateLabel(event.date)}
                    </p>
                    <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                      {event.date}
                      {event.amount ? ` - ${formatMoney(event.amount, currency)}` : ""}
                    </p>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </section>
    </AppLayout>
  );
}
